import { NavMarker } from 'react-rhumb';
import { Table, SortTH } from 'foundations-components/transitional/components/Table';
import FormattedMessage from 'I18n/components/FormattedMessage';
import ApiError from '../../../../components/Errors/ApiError';
import { useCertificationAggregationsByStatus } from '../../myTeamQueries';
import ACTableSkeleton from 'academy-common-lib/components/loading/ACTableSkeleton';
import { useTableSorting } from '../../hooks/tableHooks';
import { useCertificationsTableStore, useSyncTableStateWithUrl } from '../../store/myTeamTableStateStore';
import EmptyTable from '../common/EmptyTable';
import MyTeamTablePagination from '../common/TablePagination';
import { DEFAULT_PAGE_SIZE } from '../../constants/pagination';
import getLang from 'academy-common-lib/utils/getLang';
import CertificationLink from '../common/CertificationLink';
import { getOffsetPosition, getPaginatedPosition } from '../../utils/paginationUtils';
import { CERTIFICATIONS_PROFILE_BASE } from '../../constants/routes';
import DrilldownLink from '../common/DrilldownLink';
import { useCallback, useRef } from 'react';
import ExpiringSoonHelpIcon from '../common/ExpiringSoonHelpIcon';
import { Flex } from 'foundations-components/components/Flex';
import { useTheme } from 'foundations-theming/utils';
import { ACTableRowNoInlineRadius } from 'academy-common-lib/components/table/SharedTableComponents';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const pageSize = DEFAULT_PAGE_SIZE;
export const CertificationsTable = () => {
  const theme = useTheme();
  const certificationsTableStore = useCertificationsTableStore();
  const {
    state: {
      certificationStatuses,
      search,
      categories,
      tools,
      teams,
      users,
      sortBy,
      sortOrder,
      page
    },
    actions: {
      resetTableState,
      setSortBy,
      setSortOrder,
      setPage
    }
  } = certificationsTableStore;
  useSyncTableStateWithUrl(certificationsTableStore);
  const totalResultsRef = useRef(0);
  const {
    handleSortChange,
    getSortDirectionForColumn
  } = useTableSorting({
    sortBy,
    sortOrder,
    setSortBy,
    setSortOrder
  });
  const offsetPosition = getOffsetPosition(page, pageSize);
  const {
    certificationAggregationsByStatus,
    certificationAggregationsByStatusLoading: loading,
    certificationAggregationsByStatusError: error
  } = useCertificationAggregationsByStatus({
    certificationStatuses,
    categories,
    tools,
    teamIds: teams,
    userIds: users,
    certificationNameFilter: search,
    sortField: sortBy,
    sortOrder,
    offset: {
      offsetPosition
    },
    preferredLanguage: getLang().toUpperCase(),
    pageLength: pageSize
  });
  const certificationData = certificationAggregationsByStatus === null || certificationAggregationsByStatus === void 0 ? void 0 : certificationAggregationsByStatus.results;
  const buildDrilldownLink = useCallback((certificationId, status) => {
    const drilldownQueryString = new URLSearchParams({
      teams: teams.join(','),
      users: users.join(',')
    }).toString();
    return `${CERTIFICATIONS_PROFILE_BASE}/${certificationId}/${status}${drilldownQueryString ? `?${drilldownQueryString}` : ''}`;
  }, [teams, users]);
  if (!loading && (certificationData === null || certificationData === void 0 ? void 0 : certificationData.length) === 0) {
    return /*#__PURE__*/_jsx(EmptyTable, {
      navMarkerName: "CERTIFICATIONS_EMPTY",
      onResetClick: resetTableState
    });
  }
  if (error) {
    return /*#__PURE__*/_jsx(NavMarker, {
      name: "CERTIFICATION_API_ERROR",
      children: /*#__PURE__*/_jsx(ApiError, {
        error: {
          status: 500
        }
      })
    });
  }
  if (!loading && certificationAggregationsByStatus) {
    totalResultsRef.current = certificationAggregationsByStatus.total;
  }
  return /*#__PURE__*/_jsxs(_Fragment, {
    children: [loading ? /*#__PURE__*/_jsx(ACTableSkeleton, {
      columns: 5,
      condensed: true,
      headerRows: 2,
      rows: pageSize
    }) : certificationData && /*#__PURE__*/_jsx(NavMarker, {
      name: "CERTIFICATIONS_LOADED",
      children: /*#__PURE__*/_jsxs(Table, {
        condensed: true,
        "data-test-id": "certifications-table",
        hover: true,
        responsive: true,
        children: [/*#__PURE__*/_jsxs("colgroup", {
          children: [/*#__PURE__*/_jsx("col", {
            style: {
              width: '40%',
              borderInlineEnd: `${theme.borderWidth['100']} solid ${theme.color['border-core-subtle']}`
            }
          }), /*#__PURE__*/_jsx("col", {
            style: {
              width: '15%'
            }
          }), /*#__PURE__*/_jsx("col", {
            style: {
              width: '15%'
            }
          }), /*#__PURE__*/_jsx("col", {
            style: {
              width: '15%'
            }
          }), /*#__PURE__*/_jsx("col", {
            style: {
              width: '15%'
            }
          })]
        }), /*#__PURE__*/_jsxs("thead", {
          children: [/*#__PURE__*/_jsxs("tr", {
            children: [/*#__PURE__*/_jsx("th", {}), /*#__PURE__*/_jsx("th", {
              colSpan: 4,
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "learningCenter.myTeam.tableColumns.teamMembers"
              })
            })]
          }), /*#__PURE__*/_jsxs(ACTableRowNoInlineRadius, {
            children: [/*#__PURE__*/_jsx(SortTH, {
              onClick: () => handleSortChange('CERTIFICATION_NAME'),
              sort: getSortDirectionForColumn('CERTIFICATION_NAME'),
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "learningCenter.myTeam.tableColumns.certifications"
              })
            }), /*#__PURE__*/_jsx(SortTH, {
              onClick: () => handleSortChange('ACTIVE'),
              sort: getSortDirectionForColumn('ACTIVE'),
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "learningCenter.myTeam.tableColumns.active"
              })
            }), /*#__PURE__*/_jsx(SortTH, {
              onClick: () => handleSortChange('EXPIRING_SOON'),
              sort: getSortDirectionForColumn('EXPIRING_SOON'),
              children: /*#__PURE__*/_jsxs(Flex, {
                gap: "100",
                children: [/*#__PURE__*/_jsx(FormattedMessage, {
                  message: "learningCenter.myTeam.tableColumns.expiringSoon"
                }), /*#__PURE__*/_jsx(ExpiringSoonHelpIcon, {})]
              })
            }), /*#__PURE__*/_jsx(SortTH, {
              onClick: () => handleSortChange('EXPIRED'),
              sort: getSortDirectionForColumn('EXPIRED'),
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "learningCenter.myTeam.tableColumns.expired"
              })
            }), /*#__PURE__*/_jsx(SortTH, {
              onClick: () => handleSortChange('IN_PROGRESS'),
              sort: getSortDirectionForColumn('IN_PROGRESS'),
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: "learningCenter.myTeam.tableColumns.inProgress"
              })
            })]
          })]
        }), /*#__PURE__*/_jsx("tbody", {
          children: certificationData.map((cert, index) => {
            const position = getPaginatedPosition(offsetPosition, index);
            const {
              active,
              expiringSoon,
              expired,
              inProgress
            } = cert.certificationTotals;
            return /*#__PURE__*/_jsxs("tr", {
              "data-test-id": "certification-data-row",
              children: [/*#__PURE__*/_jsx("td", {
                children: /*#__PURE__*/_jsx(CertificationLink, {
                  certificationIconUrl: cert.certificationIconUrl,
                  certificationId: cert.certificationId,
                  certificationName: cert.certificationName,
                  position: position
                })
              }), /*#__PURE__*/_jsx("td", {
                "data-test-id": "active-count",
                children: /*#__PURE__*/_jsx(DrilldownLink, {
                  aggregationTotal: active,
                  to: buildDrilldownLink(cert.certificationId, 'active')
                })
              }), /*#__PURE__*/_jsx("td", {
                "data-test-id": "expiring-soon-count",
                children: /*#__PURE__*/_jsx(DrilldownLink, {
                  aggregationTotal: expiringSoon,
                  to: buildDrilldownLink(cert.certificationId, 'active')
                })
              }), /*#__PURE__*/_jsx("td", {
                "data-test-id": "expired-count",
                children: /*#__PURE__*/_jsx(DrilldownLink, {
                  aggregationTotal: expired,
                  to: buildDrilldownLink(cert.certificationId, 'expired')
                })
              }), /*#__PURE__*/_jsx("td", {
                "data-test-id": "in-progress-count",
                children: /*#__PURE__*/_jsx(DrilldownLink, {
                  aggregationTotal: inProgress,
                  to: buildDrilldownLink(cert.certificationId, 'in-progress')
                })
              })]
            }, cert.certificationId);
          })
        })]
      })
    }), /*#__PURE__*/_jsx(MyTeamTablePagination, {
      onPageChange: setPage,
      page: page,
      pageSize: pageSize,
      totalResults: totalResultsRef.current
    })]
  });
};